import React, { useState } from 'react';
import * as api from '../lib/api';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency, formatPercent, formatKwp, formatNumber, formatEurPerDay } from '../lib/format';
import { Euro, Gauge, Boxes, Percent, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const CHART = ['hsl(215,14%,52%)', 'hsl(205,78%,45%)', 'hsl(38,92%,50%)', 'hsl(215,10%,68%)', 'hsl(0,72%,51%)', 'hsl(262,52%,55%)'];

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

export function MixOverview({ division, rows, kpi, assumptions, sid, canWrite, onReload }) {
  const isInst = division === 'Installatori';
  const revenue = isInst ? kpi?.product_mix_revenue : kpi?.client_mix_revenue;
  const dailySales = isInst ? kpi?.daily_sales_installatori : kpi?.daily_sales_impianti;
  const marginBase = isInst ? kpi?.product_mix_weighted_margin : kpi?.client_mix_weighted_margin;
  const marginEff = isInst ? kpi?.product_mix_weighted_margin_effective : kpi?.client_mix_weighted_margin_effective;
  const price = isInst ? kpi?.price_wp_installatori : kpi?.price_kwp_impianti;
  const volumeWp = isInst ? kpi?.vol_wp_installatori : kpi?.vol_wp_impianti;
  const volumeKwp = (volumeWp || 0) / 1000;
  const covKey = isInst ? 'financial_coverage_installatori_pct' : 'financial_coverage_impianti_pct';
  const currentCov = assumptions?.[covKey] || 0;
  const priceLabel = isInst ? '€/Wp' : '€/kWp';

  const [covPct, setCovPct] = useState(String(+(currentCov * 100).toFixed(2)));
  const [saving, setSaving] = useState(false);

  const covFraction = (parseFloat(String(covPct || '0').replace(',', '.')) || 0) / 100;
  const impact = covFraction * (revenue || 0);

  const saveCoverage = async () => {
    setSaving(true);
    try {
      await api.updateScenario(sid, { assumptions: { ...(assumptions || {}), [covKey]: covFraction } });
      toast.success('Copertura finanziaria aggiornata', { description: `${formatPercent(covFraction)} → impatto MOL ${formatCurrency(impact)}` });
      onReload && onReload();
    } catch (e) {
      toast.error('Errore nel salvataggio della copertura');
    } finally {
      setSaving(false);
    }
  };

  const shareData = (rows || []).map((r) => ({ name: r.name, value: (r.share_pct || 0) * 100 }));

  return (
    <div className="space-y-6">
      {/* Riepilogo economico */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-6 lg:col-span-3"><StatCard icon={Euro} label="Ricavi divisione" value={formatCurrency(revenue)} sub={`${formatEurPerDay(dailySales)}`} testid="mix-revenue" /></div>
        <div className="col-span-6 lg:col-span-3"><StatCard icon={Gauge} label={`Prezzo medio (${priceLabel})`} value={isInst ? `${formatNumber(price, 2)} €` : formatCurrency(price)} sub={isInst ? 'Prezzo pannelli FV' : 'Prezzo impianti'} testid="mix-price" /></div>
        <div className="col-span-6 lg:col-span-3"><StatCard icon={Boxes} label={isInst ? 'Volume pannelli' : 'Volume'} value={formatKwp(volumeKwp)} testid="mix-volume" /></div>
        <div className="col-span-6 lg:col-span-3"><StatCard icon={TrendingUp} label="Margine ponderato mix" value={formatPercent(marginEff)} sub={covFraction > 0 ? `base ${formatPercent(marginBase)} + cop. ${formatPercent(covFraction)}` : "% su ricavi → EBITDA"} testid="mix-margin" /></div>
      </div>

      {/* Copertura finanziaria */}
      <Card className="p-5" data-testid="coverage-card">
        <div className="flex items-center gap-2 mb-1"><Percent className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold text-foreground">Margine copertura finanziaria</h3></div>
        <p className="text-xs text-muted-foreground mb-4">% aggiuntiva sui ricavi della divisione — compensa oneri finanziari e fabbisogno di cassa. Si somma direttamente al margine operativo (MOL).</p>
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 sm:col-span-4">
            <label className="text-xs text-muted-foreground">% Copertura</label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="number" step="0.1" value={covPct} onChange={(e) => setCovPct(e.target.value)} disabled={!canWrite} className="w-28" data-testid="coverage-input" />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <div className="col-span-6 sm:col-span-3"><div className="text-xs text-muted-foreground">× Ricavi div.</div><div className="text-lg font-semibold tabular-nums">{formatCurrency(revenue)}</div></div>
          <div className="col-span-6 sm:col-span-3"><div className="text-xs text-muted-foreground">→ Impatto MOL</div><div className="text-lg font-semibold tabular-nums text-[hsl(var(--success))]" data-testid="coverage-impact">+{formatCurrency(impact)}</div></div>
          <div className="col-span-12 sm:col-span-2">
            {canWrite && <Button onClick={saveCoverage} disabled={saving} className="w-full" data-testid="coverage-save">{saving ? 'Salvo…' : 'Applica'}</Button>}
          </div>
        </div>
      </Card>

      {/* Torta ripartizione share */}
      <Card className="p-5">
        <div className="mb-2"><h3 className="text-sm font-semibold text-foreground">Ripartizione share</h3><p className="text-xs text-muted-foreground mt-0.5">Composizione percentuale del mix</p></div>
        <div data-testid="mix-pie">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={shareData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={2}>
                {shareData.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${formatNumber(v, 1)}%`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
